import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import Helpers from "./Helpers";
import getStyles from "./styles";
import Splitter from "../Splitter";
import PDFViewer from "../PDFViewer";
import ActionButtons from "./ActionButtons";
import MainContainer from "../MainContainer";
import BirthDateInput from "./BirthDateInput";
import DirectionHandler from "../DirectionHandler";
import RegistrationNumberInput from "./RegistrationNumberInput";
import { selectText } from "../LanguageSelect/LanguageSelectSlice";
import type { FileType, Language } from "../../types";
import { useAppDispatch, useAppSelector } from "../../redux";
import { setIsLoading, selectIsValidated, selectIsPaneemTypeSelected } from "../../AppSlice";
import { MAIN_URL, AppRoutes, ReplaceParams, PublicEndpoints, SP_URL } from "../../constants";
import {
  selectBirthDate,
  checkStatusByRegNumAction,
  validateUserRegNumAction,
  selectRegistrationNumber,
  selectIsraeliId,
  setRegistrationNumber,
  setIsraeliId,
  checkStatusByIsraeliIdAction,
  validateUserIsraeliIdAction,
} from "../RegistrationPage/RegistrationPageSlice";
import { useNavigate } from "react-router-dom";
import { LockOutlined } from "@mui/icons-material";
import { Button, FormControlLabel, Grid, Link, Radio, RadioGroup, Typography } from "@mui/material";
import IsraeliIdInput from "./IsraeliIdInput";
import PaneemTypeSelector from "../PaneemTypeSelector/PaneemTypeSelectror";

export default function _RegistrationPage() {
  const styles = getStyles();

  const dispatch = useAppDispatch();

  const text = useAppSelector(selectText);
  const registrationNumber = useAppSelector(selectRegistrationNumber);
  const israeliId = useAppSelector(selectIsraeliId);
  const birthDate = useAppSelector(selectBirthDate);
  const isValidated = useAppSelector(selectIsValidated);
  const isPaneemTypeSelected = useAppSelector(selectIsPaneemTypeSelected);

  const navigate = useNavigate();
  const [loginOption, setLoginOption] = useState<"israeliId" | "regNum" | null>(null);

  const [isTooltip, setIsTooltip] = useState(false);
  const [errors, setErrors] = useState({
    registrationNumber: false,
    birthDate: false,
    israeliId: false,
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [fileURL, setFileURL] = useState<null | string>(null);
  const [isNavigated, setIsNavigated] = useState(false);

  useEffect(() => {
    if (loginOption === "regNum" && israeliId) {
      dispatch(setIsraeliId(null));
    }
    if (loginOption === "israeliId" && registrationNumber) {
      dispatch(setRegistrationNumber(null));
    }
  }, [loginOption]);

  useEffect(() => {
    switch (loginOption) {
      case "regNum": {
        if (
          errors.birthDate ||
          errors.registrationNumber ||
          !birthDate ||
          !registrationNumber ||
          !String(registrationNumber).length
        )
          setIsFormValid(false);
        else setIsFormValid(true);
        return;
      }
      case "israeliId": {
        if (errors.birthDate || errors.israeliId || !birthDate || !israeliId || !String(israeliId).length)
          setIsFormValid(false);
        else setIsFormValid(true);
        return;
      }
    }
  }, [
    israeliId,
    registrationNumber,
    birthDate,
    errors.birthDate,
    errors.registrationNumber,
    errors.israeliId,
    loginOption,
  ]);

  useEffect(() => {
    if (isValidated && !isNavigated) {
      setIsNavigated(true);
      navigate(AppRoutes.infoPage);
    }
  }, [isValidated]);

  const handleOpenTooltip = () => setIsTooltip(true);

  const handleCloseTooltip = () => (!isTooltip ? null : setIsTooltip(false));

  const handleCheckUserStatus = async () => {
    if (registrationNumber && birthDate)
      await dispatch(
        checkStatusByRegNumAction({
          registrationNumber,
          birthDate,
          language: text.language as Language,
        })
      );
    if (israeliId && birthDate)
      await dispatch(
        checkStatusByIsraeliIdAction({
          israeliId,
          birthDate,
          language: text.language as Language,
        })
      );
  };

  const handleValidateUser = async (event: FormEvent) => {
    event.preventDefault();

    if (registrationNumber && birthDate)
      await dispatch(
        validateUserRegNumAction({
          registrationNumber,
          birthDate,
          language: text.language as Language,
          requestId: 0,
        })
      );
    if (israeliId && birthDate)
      await dispatch(
        validateUserIsraeliIdAction({
          israeliId,
          birthDate,
          language: text.language as Language,
          requestId: 0,
        })
      );
  };

  const handleLoginOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLoginOption(event.target.value as "israeliId" | "regNum");
    setErrors({
      registrationNumber: false,
      birthDate: false,
      israeliId: false,
    });
  };

  const handleInitialLoginOption = (option: "israeliId" | "regNum") => setLoginOption(option);

  const handleOpenFile = (event: React.MouseEvent<HTMLButtonElement>) => {
    const name = event.currentTarget.id as FileType;

    dispatch(setIsLoading(true));

    setFileURL(
      `${MAIN_URL}${PublicEndpoints.faqs}`
        .replace(ReplaceParams.fileType, name)
        .replace(ReplaceParams.language, text.language)
    );

    setIsOpen(true);
  };

  const handleCloseFile = () => {
    setFileURL(null);
    setIsOpen(false);
  };

  return isPaneemTypeSelected ? (
    <>
      {!loginOption ? (
        <Grid container component="main" justifyContent="flex-end" onClick={handleCloseTooltip}>
          <MainContainer title={text.howToIdentify} titleVariant={"h5"}>
            <Grid container gap={5} justifyContent="center">
              <Grid xs={12} sm={12} md={12} lg={6} xl={6} item>
                <Button variant="contained" fullWidth onClick={() => handleInitialLoginOption("regNum")}>
                  {text.regNum}
                </Button>
              </Grid>

              <Grid xs={12} sm={12} md={12} lg={6} xl={6} item>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  onClick={() => handleInitialLoginOption("israeliId")}
                >
                  {text.israeliId_RESIDENTS}
                </Button>
              </Grid>
            </Grid>
          </MainContainer>
        </Grid>
      ) : (
        <Grid container component="main" justifyContent="flex-end" onClick={handleCloseTooltip}>
          <Splitter />

          <DirectionHandler>
            <MainContainer avatar={<LockOutlined />} title={text.formGreeting} titleVariant={"h5"}>
              <Grid xs={12} sm={12} md={12} lg={6} xl={6} onSubmit={handleValidateUser} component="form" item>
                <Typography variant="h6" align="center" gutterBottom>
                  {text.chooseLoginOption}
                </Typography>
                <RadioGroup
                  row
                  value={loginOption}
                  onChange={handleLoginOptionChange}
                  sx={{ justifyContent: "center", marginBottom: "1rem" }}
                >
                  <FormControlLabel value="regNum" control={<Radio />} label={text.regNum} />
                  <FormControlLabel value="israeliId" control={<Radio />} label={text.israeliId_RESIDENTS} />
                </RadioGroup>

                {loginOption === "israeliId" && (
                  <IsraeliIdInput
                    errors={errors}
                    label={text.israeliId}
                    helperText={text.israeliIdDesc}
                    israeliId={israeliId}
                    setErrors={setErrors}
                  />
                )}

                {loginOption === "regNum" && (
                  <RegistrationNumberInput
                    errors={errors}
                    label={text.regNum}
                    tooltip={text.regNumTooltip}
                    isTooltip={isTooltip}
                    helperText={text.regNumDesc}
                    registrationNumber={registrationNumber}
                    setErrors={setErrors}
                    handleCloseTooltip={handleCloseTooltip}
                    handleOpenTooltip={handleOpenTooltip}
                  />
                )}

                <BirthDateInput errors={errors} label={text.birthDate} birthDate={birthDate} setErrors={setErrors} />

                {isFormValid ? (
                  <ActionButtons
                    startText={text.startValidation}
                    statusText={text.statusCheck}
                    handleCheckUserStatus={handleCheckUserStatus}
                  />
                ) : null}

                <Typography variant="body2" align="left" sx={styles.error}>
                  {errors.registrationNumber ? `* ${text.regNumError}` : null}
                </Typography>

                <Typography variant="body2" align="left" sx={styles.error}>
                  {errors.birthDate ? `* ${text.birthDateError}` : null}
                </Typography>

                <Helpers
                  FAQ={text.FAQ}
                  instructions={text.instructions}
                  styles={styles}
                  handleOpenFile={handleOpenFile}
                />
              </Grid>

              <Typography component="h1" variant={"h6"} textAlign="center">
                {text.form}
              </Typography>
              <Typography variant="body2" align="center" sx={{ fontSize: 20 }}>
                <Link href={`${SP_URL}liveness_verification_agencies`} target="_blank">
                  {text.login_clientID}
                </Link>
              </Typography>
            </MainContainer>
          </DirectionHandler>
        </Grid>
      )}

      <PDFViewer close={handleCloseFile} file={fileURL} isOpen={isOpen} />
    </>
  ) : (
    <PaneemTypeSelector />
  );
}
